/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import React from 'react';
import { decorate, runInAction, observable, action, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';
import { displayError } from '@amzn/base-ui/dist/helpers/notification';
import Form from '@amzn/base-ui/dist/parts/helpers/fields/Form';
import Input from '@amzn/base-ui/dist/parts/helpers/fields/Input';
import TextArea from '@amzn/base-ui/dist/parts/helpers/fields/TextArea';

import { getKeyPairCreateForm } from '../../../models/forms/KeyPairCreateForm';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// expected props
// - onCancel (via props) a function is called with the user clicks on cancel or done
// - keyPairsStore (via injection)
class KeyPairCreateForm extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.form = getKeyPairCreateForm();
      this.keyPair = undefined;
    });
  }

  get keyPairsStore() { return this.props.keyPairsStore; }

  handleCreate = async form => {
    const data = form.values();
    try {
      const keyPair = await this.keyPairsStore.createKetPair(data);
      runInAction(() => { this.keyPair = keyPair; });
    } catch (error) {
      displayError(error);
    }
  };

  handleCancel = () => {
    this.keyPair = undefined;
    const onCancel = this.props.onCancel;
    if (onCancel) return onCancel();
    return undefined;
  };

  handleDownload = () => {
    const keyPair = this.keyPair;
    const downloadLink = document.createElement('a');
    downloadLink.setAttribute('href', `data:application/octet-stream,${encodeURIComponent(keyPair.privateKey)}`);
    downloadLink.setAttribute('download', `${keyPair.name}.pem`);
    downloadLink.click();
  };

  render() {
    const keyPair = this.keyPair;
    return keyPair ? this.renderPrivateKey() : this.renderForm();
  }

  renderForm() {
    const form = this.form;
    return (
      <Form form={form} onCancel={this.handleCancel} onSuccess={this.handleCreate}>
        {({ processing, onCancel }) => (
          <div className="ssh-create-form">
            <Input field={form.$('name')} className="mb3" />
            <TextArea field={form.$('desc')} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
              <button
                className="workspace-action-btn workspace-action-btn-secondary"
                disabled={processing}
                onClick={onCancel}
                type="button"
              >
                Cancel
              </button>
              <button
                className="workspace-action-btn workspace-action-btn-primary"
                disabled={processing}
                type="submit"
              >
                <Icon name="key" style={iconStyle} />
                Create Key
              </button>
            </div>
          </div>
        )}
      </Form>
    );
  }

  renderPrivateKey() {
    return (
      <div className="ssh-private-key-result">
        <div className="ssh-private-key-warning">
          <div className="ssh-private-key-warning-icon">
            <Icon name="warning sign" style={{ ...iconStyle, fontSize: '1.5em' }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, color: '#92400e', marginBottom: '4px' }}>Download Your Private Key</div>
            <div style={{ fontSize: '0.9em', color: '#92400e', lineHeight: 1.5 }}>
              This is your only chance to download the private key. It is secret information that should not
              be shared with others. Store and handle it carefully.
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
          <button
            className="workspace-action-btn workspace-action-btn-secondary"
            onClick={this.handleCancel}
          >
            Done
          </button>
          <button
            className="workspace-action-btn workspace-action-btn-primary"
            onClick={this.handleDownload}
          >
            <Icon name="download" style={iconStyle} />
            Download
          </button>
        </div>
      </div>
    );
  }
}

decorate(KeyPairCreateForm, {
  form: observable,
  keyPair: observable,
  keyPairsStore: computed,
  handleCreate: action,
  handleCancel: action,
  handleDownload: action,
});

export default inject('keyPairsStore')(withRouter(observer(KeyPairCreateForm)));
